/**
 * Persist in-progress GPS trace vertices locally so refresh or panel close
 * does not lose collected field data. Server sync is intentionally deferred.
 */

import {
  DEFAULT_TRACE_MIN_DISTANCE_M,
  TRACE_DISTANCE_OPTIONS_M,
  type TraceCaptureMode,
  type TraceDistanceOptionM,
  type TraceSamplingMode,
} from './fieldMappingGps';

export const FIELD_TRACE_DRAFT_STORAGE_KEY = 'coordal-field-trace-draft';

/** Drop drafts older than this — field sessions should finish same day/week. */
export const FIELD_TRACE_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type FieldTracePoint = { lat: number; lng: number };

export type FieldTraceDraft = {
  points: FieldTracePoint[];
  featureTypeId: string | null;
  landmarkGroup: string | null;
  samplingMode: TraceSamplingMode;
  minDistanceM: TraceDistanceOptionM;
  ringMode: boolean;
  /** When true, stop-trace post-process evenly spaces vertices along the cleaned path. */
  resampleOnStop: boolean;
  /** Continuous walk trace vs one GPS fix per corner/bend. */
  captureMode: TraceCaptureMode;
  savedAt: number;
};

function isValidPoint(value: unknown): value is FieldTracePoint {
  if (!value || typeof value !== 'object') return false;
  const p = value as FieldTracePoint;
  return Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

function isTraceSamplingMode(value: unknown): value is TraceSamplingMode {
  return value === 'distance' || value === 'always';
}

function isTraceDistanceOptionM(value: unknown): value is TraceDistanceOptionM {
  return typeof value === 'number' && (TRACE_DISTANCE_OPTIONS_M as readonly number[]).includes(value);
}

function isTraceCaptureMode(value: unknown): value is TraceCaptureMode {
  return value === 'continuous' || value === 'corners';
}

function normalizeDraft(raw: unknown): FieldTraceDraft | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Partial<FieldTraceDraft>;
  if (!Array.isArray(d.points) || d.points.length === 0) return null;
  const points = d.points.filter(isValidPoint);
  if (points.length === 0) return null;
  const savedAt = typeof d.savedAt === 'number' && Number.isFinite(d.savedAt) ? d.savedAt : Date.now();
  if (Date.now() - savedAt > FIELD_TRACE_DRAFT_MAX_AGE_MS) return null;

  return {
    points,
    featureTypeId: typeof d.featureTypeId === 'string' && d.featureTypeId.trim() ? d.featureTypeId.trim() : null,
    landmarkGroup: typeof d.landmarkGroup === 'string' && d.landmarkGroup.trim() ? d.landmarkGroup.trim() : null,
    samplingMode: isTraceSamplingMode(d.samplingMode) ? d.samplingMode : 'distance',
    minDistanceM: isTraceDistanceOptionM(d.minDistanceM) ? d.minDistanceM : DEFAULT_TRACE_MIN_DISTANCE_M,
    ringMode: d.ringMode === true,
    resampleOnStop: d.resampleOnStop === true,
    captureMode: isTraceCaptureMode(d.captureMode) ? d.captureMode : 'continuous',
    savedAt,
  };
}

export function readFieldTraceDraft(): FieldTraceDraft | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(FIELD_TRACE_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return normalizeDraft(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeFieldTraceDraft(draft: FieldTraceDraft): void {
  try {
    if (typeof window === 'undefined') return;
    if (!draft.points.length) {
      clearFieldTraceDraft();
      return;
    }
    localStorage.setItem(
      FIELD_TRACE_DRAFT_STORAGE_KEY,
      JSON.stringify({
        ...draft,
        savedAt: draft.savedAt || Date.now(),
      }),
    );
  } catch {
    /* private mode / quota */
  }
}

export function clearFieldTraceDraft(): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(FIELD_TRACE_DRAFT_STORAGE_KEY);
  } catch {
    /* noop */
  }
}
